import ClipperLib from 'clipper-lib'
import L from "leaflet";

/**
 * 地图交叉区域计算和显示工具类
 */
export default {
  /**
   * 查找并显示所有交叉区域
   * @param {Object} map - Leaflet地图实例
   * @param {Array} layers - 需要计算交叉的图层数组
   * @param {Array} existingIntersectionLayers - 现有的交叉区域图层数组，将被清除
   * @returns {Array} 新创建的交叉区域图层数组
   */
  findAndDisplayIntersections(map, layers, existingIntersectionLayers = []) {
    // 清除之前的交叉区域图层
    if (existingIntersectionLayers && existingIntersectionLayers.length > 0) {
      existingIntersectionLayers.forEach(layer => {
        map.removeLayer(layer);
      });
    }
    const intersectionLayers = [];
    
    // 按照优先级排序区域列表 - 使用新的通用排序方法
    const sortedLayers = this.sortAreasByPriority(layers, true); // true表示这是图层对象
    
    // 计算所有可能的交叉区域
    for (let i = 0; i < sortedLayers.length; i++) {
      for (let j = i + 1; j < sortedLayers.length; j++) {
        const layer1 = sortedLayers[i]; // 低优先级图层
        const layer2 = sortedLayers[j]; // 高优先级图层
        
        try {
          // 获取两个多边形的坐标
          const poly1 = layer1._latlngs[0];
          const poly2 = layer2._latlngs[0];
          
          // 检查两个图层的优先级是否相同
          const priority1 = layer1.options.layerPriority || 0;
          const priority2 = layer2.options.layerPriority || 0;
          const isSamePriority = Math.abs(priority1 - priority2) < 0.01;
          
          // 如果是同优先级图层交叉，则跳过显示网格
          if (isSamePriority) {
            //console.warn('检测到同优先级图层交叉，跳过显示网格:')
              continue;
          }
          
          // 计算相交区域
          const intersection = this.calculateIntersection(poly1, poly2);
          
          if (intersection && intersection.length > 0) {
          // 获取颜色：使用 fillColor 而非 color，避免合并选中时描边变紫色影响交叉区域图案
            const layer1Color = layer1.options.fillColor || layer1.options.color;
            const layer2Color = layer2.options.fillColor || layer2.options.color;
            
            // 创建交叉区域图层
            intersection.forEach(intersectionPoints => {
              // 创建交叉区域图层，不显示边框
              const intersectionLayer = L.polygon(intersectionPoints, {
                color: 'transparent', // 设置边框颜色为透明
                fillColor: '#FFFFFF', // 先设置为白色，后面会通过SVG样式修改
                fillOpacity: 1.0, // 完全不透明
                weight: 0, // 设置边框宽度为0
                className: 'intersection-layer', // 添加特殊类名
                pmIgnore: true, // 告诉 leaflet.pm 忽略此图层
                interactive: false // 禁用交互
              }).addTo(map);
              
              // 获取SVG路径元素
              const path = intersectionLayer._path;
              if (path) {
                // 创建高对比度的背景和线条
                this.createHighContrastPattern(path, layer2Color, layer1Color);
              }
              
              // 确保交叉区域不可编辑
              this.makeLayerNonEditable(intersectionLayer);
              
              intersectionLayers.push(intersectionLayer);
            });
          }
        } catch (e) {
          console.error("计算相交区域出错:", e);
        }
      }
    }
    
    // 添加CSS样式，确保交叉区域不可编辑
    this.addNonEditableStyle();
    
    return intersectionLayers;
  },
  
  /**
   * 计算两个多边形的相交区域
   * @param {Array} poly1 - 第一个多边形的坐标点数组
   * @param {Array} poly2 - 第二个多边形的坐标点数组
   * @returns {Array|null} 相交区域的坐标点数组，如果没有相交则返回null
   */
  calculateIntersection(poly1, poly2) {
    // 使用 ClipperLib 计算相交区域
    try {
      // 将坐标格式转换为 ClipperLib 所需的格式，并放大以提高精度
      const scale = 1000000; // 使用更大的缩放因子提高精度
      
      const clipperPoly1 = poly1.map(point => ({
        X: Math.round(point.lng * scale), 
        Y: Math.round(point.lat * scale)
      }));
      
      const clipperPoly2 = poly2.map(point => ({
        X: Math.round(point.lng * scale), 
        Y: Math.round(point.lat * scale)
      }));
      
      // 创建 ClipperLib 所需的数据结构
      const subj = new ClipperLib.Paths();
      subj.push(clipperPoly1);
      
      const clip = new ClipperLib.Paths();
      clip.push(clipperPoly2);
      
      const solution = new ClipperLib.Paths();
      
      // 创建 Clipper 对象并执行相交运算
      const c = new ClipperLib.Clipper();
      c.AddPaths(subj, ClipperLib.PolyType.ptSubject, true);
      c.AddPaths(clip, ClipperLib.PolyType.ptClip, true);
      
      // 执行相交操作
      const succeeded = c.Execute(
        ClipperLib.ClipType.ctIntersection, 
        solution,
        ClipperLib.PolyFillType.pftNonZero, 
        ClipperLib.PolyFillType.pftNonZero
      );
      
      // 如果有相交区域
      if (succeeded && solution.length > 0) {
        // 将结果转换回 Leaflet 所需的格式 [lat, lng]
        const result = solution.map(path => 
          path.map(point => ({
            lat: point.Y / scale,
            lng: point.X / scale
          }))
        );
        return result;
      }
    } catch (e) {
      console.error("ClipperLib 计算相交区域出错:", e);
      console.error(e.stack);
    }
    
    return null;
  },

  /**
   * 创建高对比度的背景和线条
   * @param {Object} element - SVG路径元素
   * @param {String} lineColor - 线条颜色
   * @param {String} backgroundColor - 背景颜色
   */
  createHighContrastPattern(element, lineColor, backgroundColor) {
    // 创建随机ID，确保不会冲突
    const patternId = `pattern-${Math.random().toString(36).substr(2, 9)}`;
    
    // 获取SVG元素
    const svgElement = element.ownerSVGElement;
    if (!svgElement) return;
    
    // 检查是否已存在defs元素
    let defsElement = svgElement.querySelector('defs');
    if (!defsElement) {
      defsElement = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      svgElement.appendChild(defsElement);
    }
    
    // 创建pattern元素
    const pattern = document.createElementNS('http://www.w3.org/2000/svg', 'pattern');
    pattern.setAttribute('id', patternId);
    pattern.setAttribute('patternUnits', 'userSpaceOnUse');
    pattern.setAttribute('width', '12');  // 增大图案尺寸
    pattern.setAttribute('height', '12'); // 增大图案尺寸
    
    // 创建半透明背景矩形，让下层边框可见
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('width', '12');
    rect.setAttribute('height', '12');
    rect.setAttribute('fill', backgroundColor);
    rect.setAttribute('fill-opacity', '0.5'); // 半透明背景
    
    // 创建两条交叉斜线，使图案更明显
    const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line1.setAttribute('x1', '0');
    line1.setAttribute('y1', '0');
    line1.setAttribute('x2', '12');
    line1.setAttribute('y2', '12');
    line1.setAttribute('stroke', lineColor);
    line1.setAttribute('stroke-width', '2.5');
    
    const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line2.setAttribute('x1', '12');
    line2.setAttribute('y1', '0');
    line2.setAttribute('x2', '0');
    line2.setAttribute('y2', '12');
    line2.setAttribute('stroke', lineColor);
    line2.setAttribute('stroke-width', '2.5');
    
    // 将元素添加到pattern中
    pattern.appendChild(rect);
    pattern.appendChild(line1);
    pattern.appendChild(line2);
    
    // 将pattern添加到defs中
    defsElement.appendChild(pattern);
    
    // 应用图案到元素
    element.setAttribute('fill', `url(#${patternId})`);
  },

  /**
   * 确保图层不可编辑
   * @param {Object} layer - Leaflet图层对象
   */
  makeLayerNonEditable(layer) {
    // 禁用 leaflet.pm 编辑功能
    if (layer.pm) {
      layer.pm._enabled = false;
      
      // 移除所有事件监听器
      layer.off('click');
      layer.off('mousedown');
      layer.off('mouseover');
      layer.off('mouseout');
      
      // 设置为不可拖动
      if (layer._path) {
        L.DomUtil.addClass(layer._path, 'leaflet-pm-no-edit');
      }
    }
    
    // 设置为不可交互
    layer.options.interactive = false;
  },

  /**
   * 添加CSS样式，确保交叉区域不可编辑
   */
  addNonEditableStyle() {
    // 检查是否已经添加了样式
    if (!document.getElementById('intersection-layer-style')) {
      // 创建样式元素
      const style = document.createElement('style');
      style.id = 'intersection-layer-style';
      style.innerHTML = `
        .intersection-layer {
          pointer-events: none !important;
        }
        .leaflet-pm-no-edit {
          pointer-events: none !important;
        }
      `;
      
      // 添加到文档头部
      document.head.appendChild(style);
    }
  },

  /**
   * 计算颜色的亮度值（0-255）
   * @param {String} color - 颜色值
   * @returns {Number} 亮度值
   */
  getColorBrightness(color) {
    const rgb = this.hexToRgb(color);
    if (!rgb) return 128; // 默认中等亮度
    
    // 使用亮度公式: 0.299*R + 0.587*G + 0.114*B
    return Math.round(0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b);
  },

  /**
   * 将十六进制颜色转换为RGB
   * @param {String} hex - 十六进制颜色值
   * @returns {Object|null} RGB对象或null
   */
  hexToRgb(hex) {
    // 如果不是十六进制颜色，尝试转换
    if (!hex.startsWith('#')) {
      hex = this.getColorValue(hex);
    }
    
    // 移除可能的 # 前缀
    hex = hex.replace(/^#/, '');
    
    // 处理简写形式 (#RGB)
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    
    // 转换为RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // 检查是否有效的RGB值
    if (isNaN(r) || isNaN(g) || isNaN(b)) {
      return null;
    }
    
    return { r, g, b };
  },

  /**
   * 处理非十六进制颜色（如RGB或颜色名称）
   * @param {String} color - 颜色值
   * @returns {String} 十六进制颜色值
   */
  getColorValue(color) {
    // 如果已经是十六进制颜色，直接返回
    if (color.startsWith('#')) {
      return color;
    }
    
    // 处理RGB颜色
    if (color.startsWith('rgb')) {
      const match = color.match(/\d+/g);
      if (match && match.length >= 3) {
        const r = parseInt(match[0]);
        const g = parseInt(match[1]);
        const b = parseInt(match[2]);
        return `#${this.componentToHex(r)}${this.componentToHex(g)}${this.componentToHex(b)}`;
      }
    }
    
    // 对于其他颜色格式（如颜色名称），使用默认颜色
    return '#FFFFFF';
  },

  /**
   * 将RGB组件转换为十六进制
   * @param {Number} c - RGB颜色分量
   * @returns {String} 十六进制颜色分量
   */
  componentToHex(c) {
    const hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  },

  /**
   * 按照优先级排序区域列表
   * @param {Array} items - 需要排序的区域列表
   * @param {Boolean} isLayer - 是否为Leaflet图层对象
   * @returns {Array} 排序后的区域列表
   */
  sortAreasByPriority(items, isLayer = false) {
    // 如果列表为空，直接返回
    if (!items || items.length === 0) {
      return [];
    }
    
    // 首先按stationId分组
    const groupsByStation = {};
    items.forEach(item => {
      const stationId = isLayer ? item.options.stationId : item.stationId;
      if (!stationId) return; // 忽略没有stationId的项目
      
      if (!groupsByStation[stationId]) {
        groupsByStation[stationId] = [];
      }
      groupsByStation[stationId].push(item);
    });
    
    // 处理每个基站分组
    const sortedGroups = [];
    Object.keys(groupsByStation).forEach(stationId => {
      const group = groupsByStation[stationId];
      
      // 直接按layerPriority排序，允许值相同的情况
      group.sort((a, b) => {
        // 获取layerPriority值
        const aSort = isLayer ? a.options.layerPriority || 0 : a.layerPriority || 0;
        const bSort = isLayer ? b.options.layerPriority || 0 : b.layerPriority || 0;
        
        // 如果layerPriority值相同，则按创建时间排序
        if (aSort === bSort) {
          const aTime = isLayer ? a.options.createTime : a.createTime;
          const bTime = isLayer ? b.options.createTime : b.createTime;
          
          if (aTime && bTime) {
            return new Date(aTime) - new Date(bTime);
          }
          return 0;
        }
        
        return aSort - bSort;
      });
      
      // 将排序后的组添加到结果中
      sortedGroups.push(...group);
    });
    
    // 处理没有stationId的项目（放在最后）
    const itemsWithoutStation = items.filter(item => {
      const stationId = isLayer ? item.options.stationId : item.stationId;
      return !stationId;
    });
    
    if (itemsWithoutStation.length > 0) {
      // 按layerPriority排序，允许值相同的情况
      itemsWithoutStation.sort((a, b) => {
        const aSort = isLayer ? a.options.layerPriority || 0 : a.layerPriority || 0;
        const bSort = isLayer ? b.options.layerPriority || 0 : b.layerPriority || 0;
        
        // 如果layerPriority值相同，则按创建时间排序
        if (aSort === bSort) {
          const aTime = isLayer ? a.options.createTime : a.createTime;
          const bTime = isLayer ? b.options.createTime : b.createTime;
          
          if (aTime && bTime) {
            return new Date(aTime) - new Date(bTime);
          }
          return 0;
        }
        
        return aSort - bSort;
      });
      
      // 添加到结果末尾
      sortedGroups.push(...itemsWithoutStation);
    }
    
    return sortedGroups;
  }
};
